import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, Upload, Loader2, CheckCircle2 } from "lucide-react";
import { BUSINESS } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOB_TYPES = ["Domestic", "Commercial", "Industrial"];
const TIMEFRAMES = ["As soon as possible", "Within 1 month", "1–3 months", "Flexible / planning ahead"];

const inputCls =
  "w-full bg-[#182330] border border-[#213042] rounded px-4 py-3 text-white text-sm focus:border-[#00c2a8] focus:outline-none transition-colors placeholder:text-[#7d96ac]";
const labelCls = "font-mono-code text-xs text-[#7d96ac] uppercase block mb-1.5 tracking-wider";

export function QuoteForm() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", postcode: "",
    job_type: "Domestic", description: "", metres: "", timeframe: "", website: "",
  });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = (e) => {
    const selected = Array.from(e.target.files || []).slice(0, 5);
    setFiles(selected);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      files.forEach((f) => data.append("files", f));
      await axios.post(`${API}/quote`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong sending your enquiry. Please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#182330] text-[#eef4f8] py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <span className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest block mb-2">// ENQUIRY PORTAL</span>
            <h2 className="font-mono-code text-[clamp(26px,3.4vw,44px)] font-bold uppercase text-white mb-4 leading-tight">
              Let&apos;s Talk About Your Project
            </h2>
            <p className="text-sm text-[#a2b8cc] leading-relaxed mb-10">
              From a single bathroom to a major commercial development, we&apos;re ready to discuss your silicone
              sealing requirements.
            </p>

            <div className="space-y-4">
              <a
                href={BUSINESS.phone1Href}
                className="flex items-center gap-4 bg-[#111923] border border-[#213042] rounded p-5 hover:border-[#00c2a8]/60 transition-colors group"
                data-testid="contact-phone1-card"
              >
                <Phone className="w-5 h-5 text-[#00c2a8] shrink-0" aria-hidden="true" />
                <span>
                  <span className="font-mono-code text-[10px] uppercase tracking-widest text-[#7d96ac] block">Telephone</span>
                  <span className="font-mono-code text-base text-white group-hover:text-[#00c2a8] transition-colors">{BUSINESS.phone1}</span>
                </span>
              </a>
              <a
                href={BUSINESS.phone2Href}
                className="flex items-center gap-4 bg-[#111923] border border-[#213042] rounded p-5 hover:border-[#00c2a8]/60 transition-colors group"
                data-testid="contact-phone2-card"
              >
                <Phone className="w-5 h-5 text-[#00c2a8] shrink-0" aria-hidden="true" />
                <span>
                  <span className="font-mono-code text-[10px] uppercase tracking-widest text-[#7d96ac] block">Alternative Line</span>
                  <span className="font-mono-code text-base text-white group-hover:text-[#00c2a8] transition-colors">{BUSINESS.phone2}</span>
                </span>
              </a>
              <a
                href={`mailto:${BUSINESS.email}`}
                className="flex items-center gap-4 bg-[#111923] border border-[#213042] rounded p-5 hover:border-[#00c2a8]/60 transition-colors group"
                data-testid="contact-email-card"
              >
                <Mail className="w-5 h-5 text-[#00c2a8] shrink-0" aria-hidden="true" />
                <span>
                  <span className="font-mono-code text-[10px] uppercase tracking-widest text-[#7d96ac] block">Email</span>
                  <span className="font-mono-code text-sm text-white group-hover:text-[#00c2a8] transition-colors break-all">{BUSINESS.email}</span>
                </span>
              </a>
              <a
                href={BUSINESS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#111923] border border-[#213042] rounded p-5 hover:border-[#00c2a8]/60 transition-colors group"
                data-testid="contact-whatsapp-card"
              >
                <MessageCircle className="w-5 h-5 text-[#00c2a8] shrink-0" aria-hidden="true" />
                <span>
                  <span className="font-mono-code text-[10px] uppercase tracking-widest text-[#7d96ac] block">WhatsApp</span>
                  <span className="font-mono-code text-base text-white group-hover:text-[#00c2a8] transition-colors">{BUSINESS.phone1}</span>
                </span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            {sent ? (
              <div className="bg-[#111923] border border-[#00c2a8]/50 rounded p-10 text-center" data-testid="quote-success">
                <CheckCircle2 className="w-12 h-12 text-[#00c2a8] mx-auto mb-6" aria-hidden="true" />
                <h3 className="font-mono-code text-xl font-bold uppercase text-white mb-3">Enquiry Received</h3>
                <p className="text-sm text-[#a2b8cc] leading-relaxed mb-8 max-w-md mx-auto">
                  Thank you — your quotation request has been sent to our team. We&apos;ll review the details and
                  come back to you shortly. Need us sooner? Call {BUSINESS.phone1}.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: "", phone: "", email: "", postcode: "", job_type: "Domestic", description: "", metres: "", timeframe: "", website: "" }); setFiles([]); }}
                  className="font-mono-code border border-[#30455e] text-white text-xs px-8 py-4 rounded uppercase tracking-wider hover:border-[#00c2a8] transition-colors"
                  data-testid="quote-another-btn"
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-[#111923] border border-[#213042] p-8 rounded space-y-5" data-testid="quote-form">
                {/* Honeypot — hidden from humans */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="q-website">Website</label>
                  <input type="text" id="q-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="q-name" className={labelCls}>Full Name *</label>
                    <input id="q-name" type="text" required maxLength={120} value={form.name} onChange={set("name")} className={inputCls} data-testid="quote-name-input" />
                  </div>
                  <div>
                    <label htmlFor="q-phone" className={labelCls}>Phone Number *</label>
                    <input id="q-phone" type="tel" required maxLength={40} value={form.phone} onChange={set("phone")} className={inputCls} data-testid="quote-phone-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="q-email" className={labelCls}>Email Address *</label>
                    <input id="q-email" type="email" required maxLength={160} value={form.email} onChange={set("email")} className={inputCls} data-testid="quote-email-input" />
                  </div>
                  <div>
                    <label htmlFor="q-postcode" className={labelCls}>Project Postcode / Location *</label>
                    <input id="q-postcode" type="text" required maxLength={40} placeholder="e.g. EH1 or Newcastle" value={form.postcode} onChange={set("postcode")} className={inputCls} data-testid="quote-postcode-input" />
                  </div>
                </div>

                <div>
                  <span className={labelCls} id="job-type-label">Job Type *</span>
                  <div className="grid grid-cols-3 gap-3" role="radiogroup" aria-labelledby="job-type-label">
                    {JOB_TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        role="radio"
                        aria-checked={form.job_type === t}
                        onClick={() => setForm((f) => ({ ...f, job_type: t }))}
                        className={`font-mono-code text-[11px] uppercase tracking-wider px-3 py-3 rounded border transition-colors ${
                          form.job_type === t
                            ? "bg-[#00c2a8] text-[#111923] border-[#00c2a8] font-bold"
                            : "border-[#30455e] text-[#a2b8cc] hover:border-[#00c2a8]"
                        }`}
                        data-testid={`quote-jobtype-${t.toLowerCase()}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="q-description" className={labelCls}>Description Of Work *</label>
                  <textarea
                    id="q-description"
                    rows={4}
                    required
                    maxLength={4000}
                    placeholder="Tell us about the surfaces, the condition of any existing silicone, and anything else that helps us quote..."
                    value={form.description}
                    onChange={set("description")}
                    className={inputCls}
                    data-testid="quote-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="q-metres" className={labelCls}>Approx. Metres / Quantity (If Known)</label>
                    <input id="q-metres" type="text" maxLength={80} placeholder="e.g. 20 linear metres" value={form.metres} onChange={set("metres")} className={inputCls} data-testid="quote-metres-input" />
                  </div>
                  <div>
                    <label htmlFor="q-timeframe" className={labelCls}>Preferred Timeframe</label>
                    <select id="q-timeframe" value={form.timeframe} onChange={set("timeframe")} className={inputCls} data-testid="quote-timeframe-select">
                      <option value="">Select…</option>
                      {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <span className={labelCls}>Photographs Or Project Drawings (Optional)</span>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-full flex items-center justify-center gap-3 border border-dashed border-[#30455e] rounded px-4 py-6 text-[#a2b8cc] hover:border-[#00c2a8] hover:text-white transition-colors"
                    data-testid="quote-upload-btn"
                  >
                    <Upload className="w-4 h-4" aria-hidden="true" />
                    <span className="font-mono-code text-xs uppercase tracking-wider">
                      {files.length > 0 ? `${files.length} file(s) selected` : "Attach up to 5 files — JPG, PNG, WEBP, HEIC or PDF"}
                    </span>
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
                    className="hidden"
                    onChange={onFiles}
                    data-testid="quote-file-input"
                  />
                  {files.length > 0 && (
                    <ul className="mt-3 space-y-1" data-testid="quote-file-list">
                      {files.map((f) => (
                        <li key={f.name} className="font-mono-code text-[11px] text-[#7d96ac] truncate">&bull; {f.name}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-3 font-mono-code bg-[#00c2a8] text-[#111923] font-bold text-xs uppercase tracking-wider py-4 rounded hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="quote-submit-btn"
                >
                  {sending && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                  {sending ? "Sending…" : "Request My Free Quote"}
                </button>

                <p className="text-center text-[11px] text-[#7d96ac] leading-relaxed" data-testid="quote-privacy-note">
                  Your details are used only to respond to this enquiry and are never shared or used for marketing.
                  Prefer to talk? Call {BUSINESS.phone1} or {BUSINESS.phone2}.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
